/**
 * UangJalanModule — Admin Uang Jalan Operational View (ODE-P07-T08).
 *
 * Renders the bounded, READ-ONLY Uang Jalan operational view over the
 * server-authoritative read model:
 *   - a filterable list (DO number prefix, status, date range, actor,
 *     reversal-eligible / reversed flags) with the REQ-011 gate state, the
 *     authoritative status and the ODE-P06-T04 duplicate/reversal control
 *     state;
 *   - a read-only detail projection (record + gate verdict + control state +
 *     REQ-076 audit summary/trail).
 *
 * Full REQ-090 state set:
 *   pending       filters not yet submitted (nothing fetched)
 *   loading       fetch in flight
 *   empty         200 with zero rows — explicit "no match", never fabricated
 *   ready         server page rendered
 *   unauthorized  401 — session missing/expired
 *   blocked       403 — role denied (server-enforced; the mirror is UX only)
 *   error         400/503/network — retryable
 *
 * REQ-071 boundary: NOTHING here computes or mutates financial state. The
 * status, the gate verdict (`progressionAllowed`) and the duplicate/reversal
 * control state come from the server projection verbatim, and no
 * approve/disburse/reverse/reconcile action is ever offered — the admin
 * surface has no mutation authority. Status is never encoded by colour alone
 * (a text label always accompanies the token), and every element carries a
 * stable `data-testid` for deterministic tests.
 *
 * Traces to: ODE-PRD-REQ-011, ODE-PRD-REQ-071, ODE-PRD-REQ-072,
 *            ODE-PRD-REQ-076, ODE-PRD-REQ-090.
 */

import { useAuth } from '../../auth/AuthContext.js';
import type {
  AdminUangJalanDetail,
  AdminUangJalanRow,
} from '../../api/client.js';
import { StateView } from '../../components/StateView.js';
import type { ModuleState } from '../state.js';
import {
  useUangJalan,
  type UangJalanDetailState,
  type UangJalanListState,
  type UseUangJalanOptions,
} from './useUangJalan.js';

/**
 * Canonical ODE-OD-012 status vocabulary offered as filter options. The server
 * validates the value against its own derived vocabulary and answers 400 for
 * anything else — the client never invents a status.
 */
const STATUS_OPTIONS: readonly string[] = [
  'CREATED',
  'APPROVED',
  'DISBURSED',
  'ACTIVE',
  'RECONCILED',
  'REJECTED',
  'CANCELLED',
  'REVERSED',
  'DISPUTED',
  'WAIVED',
  'NOT_REQUIRED',
];

/** Plain-Indonesian labels for the three REQ-011 gate states. */
const GATE_LABELS: Readonly<Record<string, string>> = {
  SATISFIED: 'Terpenuhi (progres boleh lanjut)',
  PENDING: 'Menunggu (progres ditahan)',
  BLOCKED: 'Terblokir (perlu tindakan)',
};

/** Plain-Indonesian labels for the authoritative OD-012 statuses. */
const STATUS_LABELS: Readonly<Record<string, string>> = {
  CREATED: 'Dibuat',
  APPROVED: 'Disetujui',
  DISBURSED: 'Dicairkan',
  ACTIVE: 'Aktif',
  RECONCILED: 'Terekonsiliasi',
  REJECTED: 'Ditolak',
  CANCELLED: 'Dibatalkan',
  REVERSED: 'Dibalik',
  DISPUTED: 'Disengketakan',
  WAIVED: 'Diabaikan (waived)',
  NOT_REQUIRED: 'Tidak diperlukan',
};

function statusLabel(status: string): string {
  return STATUS_LABELS[status] ?? status;
}

function gateLabel(gate: string): string {
  return GATE_LABELS[gate] ?? gate;
}

function formatAmount(amount: number | null, currency: string | null): string {
  if (amount === null) return '—';
  return `${amount} ${currency ?? ''}`.trim();
}

export interface UangJalanSeams {
  readonly list?: UseUangJalanOptions['list'];
  readonly detail?: UseUangJalanOptions['detail'];
}

export function UangJalanModule({
  seams = {},
}: { readonly seams?: UangJalanSeams } = {}): JSX.Element {
  const { session } = useAuth();
  const uangJalan = useUangJalan({
    token: session?.token ?? null,
    ...(seams.list === undefined ? {} : { list: seams.list }),
    ...(seams.detail === undefined ? {} : { detail: seams.detail }),
  });
  const { form, setForm, state, detailState } = uangJalan;

  return (
    <section aria-labelledby="uang-jalan-title" className="module">
      <h2 id="uang-jalan-title">Uang Jalan</h2>
      <p className="module-description">
        Tampilan operasional baca-saja: status Uang Jalan yang otoritatif di server,
        gerbang REQ-011, serta keadaan kontrol duplikat/reversal (REQ-071/072).
        Halaman ini tidak dapat menyetujui, mencairkan, membalik, atau merekonsiliasi.
      </p>

      <form
        className="uang-jalan-form"
        data-testid="uang-jalan-form"
        onSubmit={(event) => {
          event.preventDefault();
          uangJalan.submit();
        }}
      >
        <label>
          Nomor DO
          <input
            data-testid="filter-do-number"
            value={form.doNumber}
            onChange={(event) => setForm({ doNumber: event.target.value })}
            placeholder="mis. DO-2026-0001"
          />
        </label>
        <label>
          Status
          <select
            data-testid="filter-status"
            value={form.status}
            onChange={(event) => setForm({ status: event.target.value })}
          >
            <option value="">Semua status</option>
            {STATUS_OPTIONS.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
        </label>
        <label>
          Dari tanggal
          <input
            data-testid="filter-from"
            value={form.from}
            onChange={(event) => setForm({ from: event.target.value })}
            placeholder="YYYY-MM-DD"
          />
        </label>
        <label>
          Sampai tanggal
          <input
            data-testid="filter-to"
            value={form.to}
            onChange={(event) => setForm({ to: event.target.value })}
            placeholder="YYYY-MM-DD"
          />
        </label>
        <label>
          Aktor
          <input
            data-testid="filter-actor"
            value={form.actorId}
            onChange={(event) => setForm({ actorId: event.target.value })}
            placeholder="mis. fin-1"
          />
        </label>
        <label>
          Dapat dibalik
          <select
            data-testid="filter-reversal-eligible"
            value={form.reversalEligible}
            onChange={(event) => setForm({ reversalEligible: event.target.value })}
          >
            <option value="">Semua</option>
            <option value="true">Ya</option>
            <option value="false">Tidak</option>
          </select>
        </label>
        <label>
          Sudah dibalik
          <select
            data-testid="filter-reversed"
            value={form.reversed}
            onChange={(event) => setForm({ reversed: event.target.value })}
          >
            <option value="">Semua</option>
            <option value="true">Ya</option>
            <option value="false">Tidak</option>
          </select>
        </label>
        <label>
          Batas baris
          <input
            data-testid="filter-limit"
            value={form.limit}
            onChange={(event) => setForm({ limit: event.target.value })}
            placeholder="20"
          />
        </label>
        <div className="uang-jalan-actions">
          <button type="submit" data-testid="uang-jalan-submit">
            Cari
          </button>
          <button type="button" data-testid="uang-jalan-reset" onClick={uangJalan.reset}>
            Atur ulang
          </button>
        </div>
      </form>

      {state.kind === 'pending' ? (
        <StateView state={{ kind: 'pending' }} />
      ) : state.kind === 'ready' ? (
        <ListResults
          state={state}
          onOpenDetail={uangJalan.openDetail}
          onLoadMore={uangJalan.loadMore}
        />
      ) : (
        <StateView state={toModuleState(state)} onRetry={uangJalan.submit} />
      )}

      <DetailPanel state={detailState} onClose={uangJalan.closeDetail} />
    </section>
  );
}

/** Maps the list state to the shared REQ-090 renderer. */
function toModuleState(state: UangJalanListState): ModuleState {
  switch (state.kind) {
    case 'pending':
      return { kind: 'pending' };
    case 'loading':
      return { kind: 'loading' };
    case 'ready':
      return { kind: 'empty', payload: state.page };
    case 'unauthorized':
      return { kind: 'unauthorized' };
    case 'blocked':
      return { kind: 'blocked', message: state.message };
    case 'error':
      return { kind: 'error', message: state.message };
  }
}

function ListResults({
  state,
  onOpenDetail,
  onLoadMore,
}: {
  readonly state: Extract<UangJalanListState, { kind: 'ready' }>;
  readonly onOpenDetail: (entityId: string) => void;
  readonly onLoadMore: () => void;
}): JSX.Element {
  const { page } = state;
  if (page.rows.length === 0) {
    return (
      <div data-testid="uang-jalan-empty" className="state state-empty">
        <h3>Belum ada data</h3>
        <p>Tidak ada catatan Uang Jalan yang cocok dengan filter ini.</p>
      </div>
    );
  }
  return (
    <div data-testid="uang-jalan-results">
      <table className="overview-table">
        <thead>
          <tr>
            <th scope="col">Nomor DO</th>
            <th scope="col">Status (OD-012)</th>
            <th scope="col">Gerbang (REQ-011)</th>
            <th scope="col">Nilai</th>
            <th scope="col">Kontrol (REQ-072)</th>
            <th scope="col">Diperbarui</th>
            <th scope="col">Aksi</th>
          </tr>
        </thead>
        <tbody>
          {page.rows.map((row) => (
            <UangJalanRowView key={row.entityId} row={row} onOpenDetail={onOpenDetail} />
          ))}
        </tbody>
      </table>
      <p className="uang-jalan-asof" data-testid="uang-jalan-asof">
        {page.rows.length} baris · per {new Date(page.asOfEpochMs).toISOString()}
      </p>
      {page.hasMore && (
        <button
          type="button"
          data-testid="uang-jalan-load-more"
          onClick={onLoadMore}
          disabled={state.loadingMore}
        >
          {state.loadingMore ? 'Memuat…' : 'Muat lagi'}
        </button>
      )}
    </div>
  );
}

function UangJalanRowView({
  row,
  onOpenDetail,
}: {
  readonly row: AdminUangJalanRow;
  readonly onOpenDetail: (entityId: string) => void;
}): JSX.Element {
  return (
    <tr data-testid={`uj-row-${row.entityId}`}>
      <td>{row.doNumber ?? '—'}</td>
      <td data-testid={`uj-status-${row.entityId}`}>
        {statusLabel(row.status)} <span className="badge">{row.status}</span>
      </td>
      <td data-testid={`uj-gate-${row.entityId}`}>
        {gateLabel(row.gate)}{' '}
        <span className={row.progressionAllowed ? 'badge badge-fresh' : 'badge badge-stale'}>
          {row.gate}
        </span>
      </td>
      <td data-testid={`uj-amount-${row.entityId}`}>{formatAmount(row.amount, row.currency)}</td>
      <td data-testid={`uj-control-${row.entityId}`}>
        {row.reversed
          ? 'Sudah dibalik'
          : row.reversalEligible
            ? 'Dapat dibalik'
            : 'Tidak dapat dibalik'}
        {row.idempotencyEvents > 0 ? ` · ${row.idempotencyEvents} idempotensi` : ''}
      </td>
      <td>{row.updatedAt}</td>
      <td>
        <button
          type="button"
          data-testid={`uj-detail-${row.entityId}`}
          onClick={() => onOpenDetail(row.entityId)}
        >
          Detail
        </button>
      </td>
    </tr>
  );
}

function DetailPanel({
  state,
  onClose,
}: {
  readonly state: UangJalanDetailState;
  readonly onClose: () => void;
}): JSX.Element | null {
  if (state.kind === 'idle') return null;

  return (
    <section aria-label="Detail Uang Jalan" data-testid="uang-jalan-detail-panel" className="uj-detail">
      <div className="uj-detail-head">
        <h3>Detail Uang Jalan</h3>
        <button type="button" data-testid="uang-jalan-detail-close" onClick={onClose}>
          Tutup
        </button>
      </div>
      {state.kind === 'loading' && <StateView state={{ kind: 'loading' }} />}
      {state.kind === 'not_found' && (
        <div role="alert" data-testid="uang-jalan-detail-not-found" className="state state-error">
          <h3>Tidak ditemukan</h3>
          <p>Catatan Uang Jalan tidak dikenal.</p>
        </div>
      )}
      {state.kind === 'unauthorized' && <StateView state={{ kind: 'unauthorized' }} />}
      {state.kind === 'blocked' && <StateView state={{ kind: 'blocked', message: state.message }} />}
      {state.kind === 'error' && <StateView state={{ kind: 'error', message: state.message }} />}
      {state.kind === 'ready' && <DetailBody detail={state.detail} />}
    </section>
  );
}

function DetailBody({ detail }: { readonly detail: AdminUangJalanDetail }): JSX.Element {
  return (
    <div data-testid="uang-jalan-detail-ready">
      <dl className="uj-detail-grid">
        <dt>Nomor DO</dt>
        <dd data-testid="detail-do-number">{detail.record.doNumber ?? '—'}</dd>
        <dt>Status (OD-012)</dt>
        <dd data-testid="detail-status">
          {statusLabel(detail.record.status)} <span className="badge">{detail.record.status}</span>
        </dd>
        <dt>Nilai</dt>
        <dd data-testid="detail-amount">
          {formatAmount(detail.record.amount, detail.record.currency)}
        </dd>
        <dt>Revisi</dt>
        <dd data-testid="detail-version">{detail.record.version}</dd>
        <dt>Diperbarui</dt>
        <dd>{detail.record.updatedAt}</dd>
      </dl>

      <section aria-label="Gerbang REQ-011" data-testid="detail-gate">
        <h4>Gerbang REQ-011</h4>
        <p data-testid="detail-gate-state">{gateLabel(detail.gate.state)}</p>
        <p data-testid="detail-gate-progression">
          {detail.gate.progressionAllowed
            ? 'Progres checkpoint boleh dilanjutkan.'
            : 'Progres checkpoint tidak diizinkan.'}
        </p>
        <p data-testid="detail-gate-note">{detail.gate.note}</p>
      </section>

      <section aria-label="Kontrol duplikat dan reversal" data-testid="detail-controls">
        <h4>Kontrol duplikat/reversal (REQ-072)</h4>
        <p data-testid="detail-controls-protection">
          Perlindungan duplikat aktif: {detail.controls.duplicateProtected ? 'ya' : 'tidak'}
        </p>
        <p data-testid="detail-controls-counts">
          Idempotensi: {detail.controls.idempotencyEvents} · replay: {detail.controls.replays} ·
          ditolak: {detail.controls.deniedAttempts} · mutasi: {detail.controls.mutatedEvents}
        </p>
        <p data-testid="detail-controls-reversal">
          {detail.controls.alreadyReversed
            ? 'Sudah dibalik (REVERSED).'
            : detail.controls.reversalEligible
              ? `Dapat dibalik${detail.controls.reversalGuardCode === null ? '' : ` (guard: ${detail.controls.reversalGuardCode})`}.`
              : 'Tidak dapat dibalik dari status ini.'}
        </p>
      </section>

      <section aria-label="Ringkasan audit" data-testid="detail-audit">
        <h4>Audit (REQ-076)</h4>
        <p data-testid="detail-audit-summary">
          {detail.audit.entries} peristiwa · rantai hash {detail.audit.chainIntact ? 'utuh' : 'rusak'}
          {detail.audit.lastOperation === null ? '' : ` · terakhir ${detail.audit.lastOperation}`}
        </p>
        {detail.auditTrail.length > 0 && (
          <table className="overview-table" data-testid="detail-audit-trail">
            <thead>
              <tr>
                <th scope="col">Waktu</th>
                <th scope="col">Operasi</th>
                <th scope="col">Hasil</th>
                <th scope="col">Aktor</th>
              </tr>
            </thead>
            <tbody>
              {detail.auditTrail.map((entry) => (
                <tr key={entry.eventId} data-testid={`detail-audit-${entry.eventId}`}>
                  <td>{entry.timestamp}</td>
                  <td>{entry.operation}</td>
                  <td>{entry.outcome}</td>
                  <td>
                    {entry.actorRole}/{entry.actorId}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
